import React, { useState, useRef, useEffect } from "react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import dayjs from "dayjs";

export const DEFAULT_SHORTCUTS = [
  {
    label: "Today",
    getRange: () => {
      const t = dayjs();
      return [t.startOf("day"), t.endOf("day")];
    },
  },
  {
    label: "Yesterday",
    getRange: () => {
      const t = dayjs().subtract(1, "day");
      return [t.startOf("day"), t.endOf("day")];
    },
  },
  {
    label: "This week",
    getRange: () => {
      const t = dayjs();
      return [t.startOf("week"), t.endOf("week")];
    },
  },
  {
    label: "Last week",
    getRange: () => {
      const t = dayjs().subtract(7, "day");
      return [t.startOf("week"), t.endOf("week")];
    },
  },
  {
    label: "Last 7 days",
    getRange: () => {
      const t = dayjs();
      return [t.subtract(6, "day"), t];
    },
  },
  {
    label: "This month",
    getRange: () => {
      const t = dayjs();
      return [t.startOf("month"), t.endOf("month")];
    },
  },
  {
    label: "Last month",
    getRange: () => {
      const t = dayjs().subtract(1, "month");
      return [t.startOf("month"), t.endOf("month")];
    },
  },
  {
    label: "This year",
    getRange: () => {
      const t = dayjs();
      return [t.startOf("year"), t.endOf("year")];
    },
  },
  {
    label: "Last year",
    getRange: () => {
      const t = dayjs().subtract(1, "year");
      return [t.startOf("year"), t.endOf("year")];
    },
  },
];

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function buildMonthGrid(monthDate) {
  const start = monthDate.startOf("month").startOf("week");
  const end = monthDate.endOf("month").endOf("week");
  const days = [];
  let cur = start;
  while (cur.isBefore(end) || cur.isSame(end, "day")) {
    days.push(cur);
    cur = cur.add(1, "day");
  }
  return days;
}

function MonthGrid({ monthDate, onNavigate, showPrevArrow, showNextArrow, range, hoverDate, onDayClick, onDayDoubleClick, onDayHover, isDayDisabled }) {
  const days = buildMonthGrid(monthDate);
  const [rangeStart, rangeEnd] = range;

  const isInRange = (d) => {
    if (!rangeStart) return false;
    const end = rangeEnd || hoverDate;
    if (!end) return false;
    const lo = rangeStart.isBefore(end) ? rangeStart : end;
    const hi = rangeStart.isBefore(end) ? end : rangeStart;
    return d.isAfter(lo, "day") && d.isBefore(hi, "day");
  };

  const isEndpoint = (d) => (rangeStart && d.isSame(rangeStart, "day")) || (rangeEnd && d.isSame(rangeEnd, "day"));

  // Track click timing to distinguish single vs double click ourselves,
  // since native onDoubleClick also fires two onClick events first.
  const clickTimer = useRef(null);

  const handleClick = (d, disabled) => {
    if (disabled) return;
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
      onDayDoubleClick(d);
    } else {
      clickTimer.current = setTimeout(() => {
        onDayClick(d);
        clickTimer.current = null;
      }, 220);
    }
  };

  return (
    <div className="w-[280px]">
      <div className="flex items-center justify-between px-2 mb-2">
        <button onClick={() => showPrevArrow && onNavigate(-1)} className={`p-1 rounded hover:bg-gray-100 ${!showPrevArrow ? "invisible" : ""}`}>
          <ChevronLeft size={16} className="text-gray-400" />
        </button>
        <span className="text-sm font-medium text-gray-800">{monthDate.format("MMM YYYY")}</span>
        <button onClick={() => showNextArrow && onNavigate(1)} className={`p-1 rounded hover:bg-gray-100 ${!showNextArrow ? "invisible" : ""}`}>
          <ChevronRight size={16} className="text-gray-400" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-y-1 px-2">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-center text-[11px] text-gray-400 font-medium py-1">
            {w}
          </div>
        ))}

        {days.map((d) => {
          const outside = !d.isSame(monthDate, "month");
          const inRange = isInRange(d);
          const endpoint = isEndpoint(d);
          const disabled = isDayDisabled(d);

          return (
            <div key={d.format("YYYY-MM-DD")} className="flex items-center justify-center relative">
              {inRange && !disabled && <div className="absolute inset-y-0 left-0 right-0 bg-sky-50" />}
              <button
                onClick={() => handleClick(d, disabled)}
                onMouseEnter={() => !disabled && onDayHover(d)}
                disabled={disabled}
                className={`relative z-10 w-7 h-7 text-xs rounded-full transition-colors
                  ${disabled ? "text-gray-200 cursor-not-allowed" : outside ? "text-gray-300" : "text-gray-700"}
                  ${endpoint && !disabled ? "bg-blue-600 text-white font-medium" : ""}
                  ${!endpoint && !outside && !disabled ? "hover:bg-sky-100" : ""}
                `}
              >
                {d.date()}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * DateRangeField
 *
 * Props:
 * - from, to: "YYYY-MM-DD" strings (controlled)
 * - onCustomRangeChange({ from, to }): fired on selection completion, or clear
 * - disableToday: boolean — disallow selecting today
 * - disableFuture: boolean — disallow any date after today
 * - disablePast: boolean — disallow any date before today
 * - minDate, maxDate: optional "YYYY-MM-DD" hard bounds, in addition to the above
 * - shortcuts: optional array of { label, getRange: () => [dayjs, dayjs] } to override DEFAULT_SHORTCUTS
 * - defaultRange: optional () => [dayjs, dayjs] — used to seed the initial rangeStart/rangeEnd
 *   ONLY when neither `from` nor `to` is provided by the parent. Does NOT auto-fire
 *   onCustomRangeChange — it just pre-fills the picker's display and calendar state.
 */
export default function DateRangeField({ from, to, onCustomRangeChange, disableToday = false, disableFuture = false, disablePast = false, minDate = null, maxDate = null, shortcuts = DEFAULT_SHORTCUTS, defaultRange = null }) {
  // Resolve initial values: prefer explicit from/to props, else fall back to defaultRange
  const getInitialRange = () => {
    if (from || to) {
      return [from ? dayjs(from) : null, to ? dayjs(to) : null];
    }
    if (defaultRange) {
      const [s, e] = defaultRange();
      return [s || null, e || null];
    }
    return [null, null];
  };

  const [initialStart, initialEnd] = getInitialRange();

  const [open, setOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState((initialStart || dayjs()).startOf("month"));
  const [rangeStart, setRangeStart] = useState(initialStart);
  const [rangeEnd, setRangeEnd] = useState(initialEnd);
  const [hoverDate, setHoverDate] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    // Only re-sync from props when the parent actually has values —
    // avoids stomping the defaultRange fallback with empty props on every render
    if (from || to) {
      setRangeStart(from ? dayjs(from) : null);
      setRangeEnd(to ? dayjs(to) : null);
    }
  }, [from, to]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isDayDisabled = (d) => {
    const today = dayjs().startOf("day");
    if (disableToday && d.isSame(today, "day")) return true;
    if (disableFuture && d.isAfter(today, "day")) return true;
    if (disablePast && d.isBefore(today, "day")) return true;
    if (minDate && d.isBefore(dayjs(minDate), "day")) return true;
    if (maxDate && d.isAfter(dayjs(maxDate), "day")) return true;
    return false;
  };

  // Fires the parent callback and closes the popup — the single place
  // a completed range gets committed, whether via click, double-click,
  // shortcut, or clear.
  const commitRange = (s, e) => {
    onCustomRangeChange({
      from: s ? s.format("YYYY-MM-DD") : "",
      to: e ? e.format("YYYY-MM-DD") : "",
    });
    setOpen(false);
  };

  // Single click:
  // - No start yet, OR both start+end already set (starting a fresh selection) → set as new start, clear end
  // - Start set, no end yet → this is the 2nd click: decide from/to by comparing to start,
  //   then commit immediately and close
  const handleDayClick = (d) => {
    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(d);
      setRangeEnd(null);
      return;
    }

    let finalStart = rangeStart;
    let finalEnd = d;

    if (d.isBefore(rangeStart, "day")) {
      // Clicked date is before the first pick — it becomes the new "from",
      // and the original first pick moves to "to"
      finalStart = d;
      finalEnd = rangeStart;
    }

    setRangeStart(finalStart);
    setRangeEnd(finalEnd);
    commitRange(finalStart, finalEnd);
  };

  // Double click: select just that single day as both from and to, commit immediately
  const handleDayDoubleClick = (d) => {
    setRangeStart(d);
    setRangeEnd(d);
    commitRange(d, d);
  };

  const applyShortcut = (getRange) => {
    const [s, e] = getRange();
    setRangeStart(s);
    setRangeEnd(e);
    setVisibleMonth(s.startOf("month"));
    commitRange(s, e);
  };

  // Clears the selection, notifies the parent with empty values, and closes
  const handleClear = () => {
    setRangeStart(null);
    setRangeEnd(null);
    commitRange(null, null);
  };

  const secondMonth = visibleMonth.add(1, "month");

  // Reflect the LIVE selection (rangeStart/rangeEnd), not just the committed from/to props,
  // so the button label updates the moment you pick the first date.
  const displayFrom = rangeStart ? rangeStart.format("YYYY-MM-DD") : "Start date";
  const displayTo = rangeEnd ? rangeEnd.format("YYYY-MM-DD") : "End date";

  return (
    <div className="relative" ref={wrapperRef}>
      <button onClick={() => setOpen((v) => !v)} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs border transition-colors ${open ? "border-sky-400" : "border-gray-200 hover:bg-gray-50"}`}>
        <span className="text-gray-700">{displayFrom}</span>
        <span className="text-gray-400">→</span>
        <span className="text-gray-700">{displayTo}</span>
        <CalendarIcon size={14} className="text-gray-400" />
      </button>

      {open && (
        <div className="absolute z-20 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          <div className="flex">
            <MonthGrid monthDate={visibleMonth} onNavigate={(dir) => setVisibleMonth((m) => m.add(dir, "month"))} showPrevArrow showNextArrow={false} range={[rangeStart, rangeEnd]} hoverDate={hoverDate} onDayClick={handleDayClick} onDayDoubleClick={handleDayDoubleClick} onDayHover={setHoverDate} isDayDisabled={isDayDisabled} />
            <div className="w-px bg-gray-100" />
            <MonthGrid monthDate={secondMonth} onNavigate={(dir) => setVisibleMonth((m) => m.add(dir, "month"))} showPrevArrow={false} showNextArrow range={[rangeStart, rangeEnd]} hoverDate={hoverDate} onDayClick={handleDayClick} onDayDoubleClick={handleDayDoubleClick} onDayHover={setHoverDate} isDayDisabled={isDayDisabled} />
          </div>

          {(shortcuts.length > 0 || rangeStart || rangeEnd) && (
            <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 border-t border-gray-100">
              {shortcuts.map((s) => (
                <button key={s.label} onClick={() => applyShortcut(s.getRange)} className="px-2.5 py-1 text-xs rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50">
                  {s.label}
                </button>
              ))}

              {(rangeStart || rangeEnd) && (
                <button onClick={handleClear} className="px-2.5 py-1 text-xs rounded-md border border-gray-200 text-red-500 hover:bg-red-50 ml-auto">
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
